import { useState } from "react";
import api, { fmtINR, formatApiError } from "@/lib/api";
import { Calculator, Loader2 } from "lucide-react";

const inputCls =
  "w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)]";
const labelCls =
  "block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1";

function WeightPill({ label, value, active, emphasize }) {
  const style = emphasize
    ? "border-[color:var(--neon-cyan)] bg-[color:var(--neon-cyan)]/20 text-[color:var(--neon-cyan)]"
    : active
    ? "border-[color:var(--mecha-red)] bg-[color:var(--mecha-red)]/15 text-white"
    : "border-zinc-800 text-zinc-500";
  return (
    <div className={`border p-2 text-center ${style}`}>
      <div className="font-mono text-[9px] tracking-widest">{label}</div>
      <div className="font-mono font-bold text-sm mt-0.5">{value}</div>
    </div>
  );
}

export default function DeliveryEstimate() {
  const [form, setForm] = useState({
    destination_pincode: "",
    weight: "500",
    length: "",
    width: "",
    height: "",
    payment_mode: "Pre-paid",
    cod_amount: "",
    shipping_mode: "Surface",
  });
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setResult(null);
    setError(null);
  };

  const estimate = async (e) => {
    e.preventDefault();
    const pin = form.destination_pincode.trim();
    if (!/^\d{6}$/.test(pin)) {
      setError("Enter a valid 6-digit destination pincode");
      setResult(null);
      return;
    }
    setCalculating(true);
    setError(null);
    setResult(null);
    try {
      const payload = {
        destination_pincode: pin,
        weight_grams: parseInt(form.weight, 10) || 500,
        payment_mode: form.payment_mode,
        shipping_mode: form.shipping_mode || "Surface",
      };
      if (form.payment_mode === "COD") {
        payload.cod_amount = parseFloat(form.cod_amount) || 0;
      }
      const L = parseFloat(form.length);
      const W = parseFloat(form.width);
      const H = parseFloat(form.height);
      if (L > 0 && W > 0 && H > 0) {
        payload.length_cm = L;
        payload.width_cm = W;
        payload.height_cm = H;
      }
      const { data } = await api.post("/api/shipping/rate", payload);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12">
      <div className="mb-8">
        <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
          // FREIGHT
        </div>
        <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl mt-3 leading-none">
          DELIVERY ESTIMATE
        </h1>
        <p className="mt-4 text-zinc-400 text-sm max-w-xl">
          Quote Delhivery Surface / Express shipping before you take an order.
          Uses your store pincode as origin.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6 max-w-5xl">
        <form
          onSubmit={estimate}
          className="card-brutal lg:col-span-3"
          data-testid="delivery-estimate-form"
        >
          <div className="p-5 border-b border-zinc-800 flex items-center gap-2">
            <Calculator className="h-4 w-4 text-[color:var(--mecha-red)]" />
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
              RATE CALCULATOR
            </div>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>DESTINATION PINCODE</label>
                <input
                  data-testid="est-pincode"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  placeholder="6 digits"
                  value={form.destination_pincode}
                  onChange={set("destination_pincode")}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>ACTUAL WEIGHT (G)</label>
                <input
                  data-testid="est-weight"
                  type="number"
                  min="1"
                  required
                  value={form.weight}
                  onChange={set("weight")}
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>PARCEL DIMENSIONS · CM (OPTIONAL)</label>
              <div className="grid grid-cols-3 gap-2">
                <input
                  data-testid="est-length"
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="L"
                  value={form.length}
                  onChange={set("length")}
                  className={inputCls}
                />
                <input
                  data-testid="est-width"
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="W"
                  value={form.width}
                  onChange={set("width")}
                  className={inputCls}
                />
                <input
                  data-testid="est-height"
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="H"
                  value={form.height}
                  onChange={set("height")}
                  className={inputCls}
                />
              </div>
              <div className="mt-1.5 font-mono text-[10px] tracking-widest text-zinc-500">
                VOLUMETRIC = L × W × H / 5 · CHARGED = MAX(ACTUAL, VOLUMETRIC)
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>SHIPPING MODE</label>
                <select
                  data-testid="est-shipping-mode"
                  value={form.shipping_mode}
                  onChange={set("shipping_mode")}
                  className={inputCls}
                >
                  <option value="Surface">Surface (cheaper, 4–7 days)</option>
                  <option value="Express">Express (air, 2–3 days)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>PAYMENT MODE</label>
                <select
                  data-testid="est-payment-mode"
                  value={form.payment_mode}
                  onChange={set("payment_mode")}
                  className={inputCls}
                >
                  <option value="Pre-paid">Pre-paid</option>
                  <option value="COD">COD</option>
                </select>
              </div>
            </div>

            {form.payment_mode === "COD" && (
              <div>
                <label className={labelCls}>COD AMOUNT (₹)</label>
                <input
                  data-testid="est-cod-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Collect amount"
                  value={form.cod_amount}
                  onChange={set("cod_amount")}
                  className={inputCls}
                />
              </div>
            )}

            <button
              data-testid="est-submit"
              type="submit"
              disabled={calculating}
              className="btn-brutal w-full h-12 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {calculating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Estimate Delhivery Cost"
              )}
            </button>

            {error && (
              <div
                data-testid="est-error"
                className="border border-[color:var(--mecha-red)] bg-[color:var(--mecha-red)]/10 p-3 text-xs text-[color:var(--mecha-red-hover)]"
              >
                {error}
              </div>
            )}
          </div>
        </form>

        <section className="card-brutal lg:col-span-2">
          <div className="p-5 border-b border-zinc-800 font-mono text-[10px] tracking-[0.3em] text-zinc-500">
            QUOTE
          </div>
          <div className="p-5">
            {!result && !calculating && (
              <div
                data-testid="est-empty"
                className="text-zinc-500 text-sm leading-relaxed"
              >
                Enter destination pin and weight, then estimate. Results show zone,
                chargeable weight, and total with tax.
              </div>
            )}
            {calculating && (
              <div className="flex items-center gap-2 text-zinc-400 text-sm font-mono">
                <Loader2 className="h-4 w-4 animate-spin" />
                FETCHING RATE...
              </div>
            )}
            {result && (
              <div data-testid="est-result" className="space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  <WeightPill
                    label="ACTUAL"
                    value={`${result.actual_weight_g || 0} g`}
                    active={result.weight_basis === "actual"}
                  />
                  <WeightPill
                    label="VOLUMETRIC"
                    value={`${result.volumetric_weight_g || 0} g`}
                    active={result.weight_basis === "volumetric"}
                  />
                  <WeightPill
                    label="CHARGED"
                    value={`${result.chargeable_weight_g || 0} g`}
                    emphasize
                  />
                </div>

                <div className="space-y-2 text-sm border border-[color:var(--neon-cyan)]/30 bg-[color:var(--neon-cyan)]/5 p-4">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Origin</span>
                    <span className="font-mono">{result.origin_pincode || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Destination</span>
                    <span className="font-mono">{result.destination_pincode || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Mode</span>
                    <span className="font-mono">{result.shipping_mode || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Zone</span>
                    <span className="font-mono">{result.zone || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Gross</span>
                    <span className="font-mono">{fmtINR(result.gross_amount || 0)}</span>
                  </div>
                  <div className="flex justify-between items-end pt-3 border-t border-[color:var(--neon-cyan)]/40">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-400">
                      TOTAL W/ TAX
                    </span>
                    <span
                      data-testid="est-total"
                      className="font-display text-3xl text-[color:var(--neon-cyan)]"
                    >
                      {fmtINR(result.total_amount || 0)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
